import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Lock, ShieldCheck, Headphones } from 'lucide-react';

interface GuestRecorderCardProps {
  question: string;
  onClose?: () => void;
  onSaveClick: () => void;
}

const GuestRecorderCard: React.FC<GuestRecorderCardProps> = ({
  question,
  onClose,
  onSaveClick
}) => {
  const [step, setStep] = useState<'recording' | 'processing' | 'finished'>('recording');
  const [timer, setTimer] = useState(0);
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animationFrameIdRef = useRef<number | null>(null);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const drawWaveform = useCallback(() => {
    if (!analyserRef.current || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const canvasCtx = canvas.getContext('2d');
    if (!canvasCtx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();

    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    canvasCtx.scale(dpr, dpr);

    const bufferLength = analyserRef.current.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      if (step !== 'recording') return;

      animationFrameIdRef.current = requestAnimationFrame(draw);

      analyserRef.current?.getByteTimeDomainData(dataArray);

      canvasCtx.clearRect(0, 0, rect.width, rect.height);
      canvasCtx.lineWidth = 2;
      canvasCtx.strokeStyle = 'rgb(251, 113, 133)';
      canvasCtx.beginPath();

      const sliceWidth = rect.width * 1.0 / bufferLength;
      let x = 0;

      const sensitivity = 2.5;

      for (let i = 0; i < bufferLength; i++) {
        const v = dataArray[i] / 128.0;
        const amplifiedV = ((v - 1) * sensitivity) + 1;
        const y = amplifiedV * rect.height / 2;

        if (i === 0) {
          canvasCtx.moveTo(x, y);
        } else {
          canvasCtx.lineTo(x, y);
        }

        x += sliceWidth;
      }

      canvasCtx.lineTo(rect.width, rect.height / 2);
      canvasCtx.stroke();
    };

    draw();
  }, [step]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setHasPermission(true);
      mediaRecorderRef.current = new MediaRecorder(stream);
      const audioChunks: BlobPart[] = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunks.push(event.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
        console.log('Audio recorded:', audioBlob);
      };

      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      audioContextRef.current = new AudioContextClass();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      analyserRef.current = audioContextRef.current.createAnalyser();
      source.connect(analyserRef.current);
      analyserRef.current.fftSize = 256;

      drawWaveform();

      mediaRecorderRef.current.start();
      setTimer(0);
      timerRef.current = setInterval(() => {
        setTimer(t => t + 1);
      }, 1000);
    } catch (error) {
      console.error('Error al acceder al micrófono:', error);
      setHasPermission(false);
    }
  };

  const stopRecording = () => {
    if (timerRef.current) clearInterval(timerRef.current);

    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
    }
    if (animationFrameIdRef.current) {
      cancelAnimationFrame(animationFrameIdRef.current);
    }
    setStep('processing');

    setTimeout(() => {
      setStep('finished');
    }, 1500);
  };

  useEffect(() => {
    if (step === 'recording') {
      startRecording();
    }
    return () => {
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  return (
    <div className="bg-white rounded-3xl shadow-2xl shadow-stone-900/10 overflow-hidden border border-stone-100 relative w-full max-w-md mx-auto">
      <div className="h-1.5 w-full bg-gradient-to-r from-rose-300 via-rose-500 to-rose-300"></div>

      <div className="p-6 md:p-8">
        {step === 'recording' && (
          <div className="text-center animate-fade-in">
            <div className="bg-stone-50 p-6 rounded-2xl border border-stone-100 mb-8 relative">
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-white text-stone-500 text-[10px] font-bold px-3 py-1 rounded-full border border-stone-100 shadow-sm uppercase tracking-widest">
                Tema Actual
              </span>
              <p className="text-xl md:text-2xl font-serif text-stone-800 leading-snug pt-2">
                "{question}"
              </p>
            </div>

            {hasPermission === false && (
              <div className="mb-4 text-amber-600 bg-amber-50 p-3 rounded text-sm">
                ⚠️ Necesitamos permiso del micrófono para grabar.
              </div>
            )}

            <div className="flex flex-col items-center justify-center mb-8">
              <div className="text-[10px] font-bold text-rose-500 tracking-widest uppercase mb-2 flex items-center gap-2">
                <span className="w-2 h-2 bg-rose-500 rounded-full animate-pulse"></span>
                Grabando
              </div>
              <div className="text-6xl font-sans font-light text-stone-900 tabular-nums tracking-tighter">
                {formatTime(timer)}
              </div>
            </div>

            <div className="h-16 w-full mb-8 bg-stone-50 rounded-xl overflow-hidden relative border border-stone-100/50">
              <canvas ref={canvasRef} width={400} height={64} className="w-full h-full" />
            </div>

            <button
              onClick={stopRecording}
              className="w-16 h-16 bg-white border-[3px] border-rose-500 rounded-full flex items-center justify-center text-rose-500 hover:bg-rose-50 hover:scale-105 transition-all mx-auto shadow-lg group"
            >
              <div className="w-6 h-6 bg-rose-500 rounded-sm group-hover:rounded-md transition-all duration-300" />
            </button>
            <p className="mt-4 text-sm text-stone-400 font-medium">Pulsa para finalizar</p>
          </div>
        )}

        {step === 'processing' && (
          <div className="text-center py-12 animate-fade-in flex flex-col items-center">
            <div className="w-16 h-16 border-4 border-rose-100 border-t-rose-500 rounded-full animate-spin mb-6"></div>
            <h3 className="text-lg font-bold text-stone-800 mb-2">Guardando historia...</h3>
            <p className="text-stone-500 text-sm">Estamos procesando el audio para que se escuche perfecto.</p>
          </div>
        )}

        {step === 'finished' && (
          <div className="animate-slide-up text-left">
            <div className="flex items-center gap-2 mb-4 text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-full w-fit border border-emerald-100">
              <ShieldCheck size={14} />
              <span className="font-bold text-[10px] uppercase tracking-wide">Captura Exitosa</span>
            </div>

            <h3 className="text-2xl font-serif font-bold text-stone-900 mb-2">
              Recuerdo capturado
            </h3>

            <div className="bg-stone-50 p-5 rounded-2xl border border-stone-100 mb-6 relative overflow-hidden group hover:bg-white hover:shadow-md transition-all">
              <div className="flex gap-4 mb-3 border-b border-stone-200 pb-3">
                <div className="w-10 h-10 rounded-full bg-stone-200 flex items-center justify-center text-stone-500">
                  <Headphones size={20} />
                </div>
                <div>
                  <div className="text-sm font-bold text-stone-800">Audio Original</div>
                  <div className="text-xs text-stone-500">{formatTime(timer)} • Calidad Estudio</div>
                </div>
              </div>
              <p className="text-stone-600 text-sm leading-relaxed font-serif italic line-clamp-3">
                "Bueno, creo que si tuviera que elegir uno, sería el verano del 82. Fuimos todos a la costa en aquel coche viejo de papá..."
              </p>
              <div className="absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-stone-50 to-transparent group-hover:from-white" />
            </div>

            <div className="space-y-3">
              <button
                onClick={onSaveClick}
                className="w-full bg-stone-900 text-white py-3.5 rounded-xl font-bold text-sm hover:bg-stone-800 hover:shadow-lg transition-all flex items-center justify-center gap-2"
              >
                <Lock size={16} /> Guardar en mi baúl privado
              </button>
              <button
                onClick={onClose}
                className="w-full text-stone-500 py-2.5 text-xs hover:text-stone-800 transition font-medium"
              >
                Descartar y grabar de nuevo
              </button>
            </div>
          </div>
        )}
      </div>
      {onClose && (
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-stone-300 hover:text-stone-500 transition p-2 hover:bg-stone-100 rounded-full"
        >
          <X size={20} />
        </button>
      )}
    </div>
  );
};

export default GuestRecorderCard;
